import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type { LostReasonRequest } from './lead/lost-reason/lost-reason-catalog';
import {
    ClientStageTransitionDto,
    ClientStageTransitionDtoIHandleError,
    ContactServiceProxy,
    UpdateContactTypeStageInputDto,
} from '@shared/service-proxies/service-proxies';

/** The slice of a client stage the relationship save reads. A `ClientStageDto` satisfies it as-is. */
export interface ClientStageRef {
    id: number | undefined;
    stage: string | undefined;
    /** SrcClientStage.Act: 0 None, 1 Won, 2 Lost. */
    act: number | undefined;
}

/** Input of {@link ClientStageCommitService.saveRelationship} — a PENDING relationship edit to persist. */
export interface ClientRelationshipSaveInput {
    contactId: number;
    /** BOTH columns are always sent: UpdateContactTypeStage overwrites both, so a partial payload clears one. */
    contactTypeId: number | undefined;
    stage: ClientStageRef | null;
    /** The stage the contact had before this edit — decides whether the stage actually changed. */
    previousStageId: number | undefined;
    /** Configurable lost reasons (E10): the reason for a move into a Lost stage (the relationship editor's Why? block). */
    lostReason?: LostReasonRequest;
    /**
     * The OPEN task the edit is saved from (the follow-up popup's task). Never sent — UpdateContactTypeStage takes no
     * task. It only reads the server's transition back: on Won or Lost the transition ends the contact's designated
     * lead follow-up, and `taskCompleted` says whether that was THIS task.
     */
    taskId?: number;
}

/** Result of {@link ClientStageCommitService.saveRelationship}. */
export interface ClientRelationshipSaveResult {
    /** Both columns were persisted. False => nothing was saved and `errorMessage` says why. */
    saved: boolean;
    /** Why the save failed; null when `saved`. */
    errorMessage: string | null;
    /** The saved stage's Act (0 None, 1 Won, 2 Lost). */
    act: number;
    /** The stage id differs from `previousStageId` (a type-only edit is false). */
    stageChanged: boolean;
    /**
     * The stage MOVED to Won or Lost and the server's transition ended AND completed `taskId` as the contact's
     * designated lead follow-up ({@link transitionCompletedTask}). The caller must NOT complete it again. False when
     * the transition ended another task (or none), or its completion of `taskId` was blocked or failed: `taskId` is
     * then still open and the caller completes it itself (surfacing any blocker). Never true without a stage move.
     */
    taskCompleted: boolean;
    /** What the server's stage transition did (type change, project prompt); undefined when it was not a transition. */
    transition: ClientStageTransitionDto | undefined;
}

/** A terminal stage Act: SrcClientStage.Act 1 Won or 2 Lost (0 None is an open stage). */
export function isTerminalStageAct(act: number | undefined): boolean {
    return act === 1 || act === 2;
}

/**
 * A Lost stage Act (2): the one client whose completions skip every follow-up popup (TC-33 as the user narrowed it on
 * 2026-10-28, FOLLOWUP_EVERY_TASK_PLAN.md: a Won client gets them).
 */
export function isLostStageAct(act: number | undefined): boolean {
    return act === 2;
}

/** "Won" / "Lost" for a terminal stage Act; '' for an open (or unknown) one. */
export function terminalStageActLabel(act: number | undefined): string {
    switch (act) {
        case 1:
            return 'Won';
        case 2:
            return 'Lost';
        default:
            return '';
    }
}

/**
 * THE cross-layer test for "the server completed THIS task on a stage save". A Won or Lost move ends the contact's
 * designated lead follow-up (ClientStageTransitionManager.EndLeadFollowUpAsync resolves its schedule / reply monitor),
 * ClientStageTransitionRunner completes that task after its commit, and the transition names it as endedLeadTaskId.
 * `leadTaskCompleted` is the runner's actual result (completed now, or already completed): a completion that was
 * blocked (an incomplete blocker) or failed reads NOT completed, so the caller completes the task itself and shows the
 * blocker. `leadFollowUpEnded` alone only means the comeback was cancelled, never that the task was completed.
 * Every surface that saves a stage from a task (the follow-up popup, the after-reply composer, the task panel)
 * decides with this and nothing else.
 */
export function transitionCompletedTask(
    transition: ClientStageTransitionDto | undefined,
    taskId: number | undefined
): boolean {
    return (
        !!transition &&
        transition.leadTaskCompleted === true &&
        !!taskId &&
        Number(transition.endedLeadTaskId) === Number(taskId)
    );
}

/**
 * The relationship (client type + stage) save for the surfaces that move a stage from a TASK — the follow-up schedule
 * popup today — and the shared reading of what the server's stage transition did to that task (open-task follow-up
 * story §8, §9; Client Settings story §10). The server transition owns every stage side effect (the client-type
 * automation, ending and completing the lead follow-up, whether to prompt for a project); this service adds none.
 *
 * It never fires `dataService.runClientStageAutomation` (the bus event that opens the global client-stage-automation
 * modal): the follow-up popup emits that event itself once it has closed, so the project prompt never stacks on top
 * of it.
 */
@Injectable({ providedIn: 'root' })
export class ClientStageCommitService {
    private static readonly SAVE_FAILED = 'Could not update the relationship.';

    constructor(private _contactService: ContactServiceProxy) {}

    /**
     * Persist a pending relationship edit (client type + stage) through UpdateContactTypeStage — the contacts UI's
     * dedicated endpoint, which patches only those two columns and runs the server's stage transition. Reads that
     * transition back into `taskCompleted` (did a Won / Lost move complete `input.taskId`?). Performs NO client-type
     * automation of its own: the caller hands the committed stage to the global client-stage-automation modal
     * (runClientStageAutomation), which acts on the transition. Never errors: an HTTP failure resolves with
     * `saved: false`.
     */
    saveRelationship(input: ClientRelationshipSaveInput): Observable<ClientRelationshipSaveResult> {
        const dto = new UpdateContactTypeStageInputDto();
        const stageId = input.stage ? input.stage.id : undefined;
        const act = input.stage ? Number(input.stage.act || 0) : 0;
        const stageChanged = stageId !== input.previousStageId;

        dto.contactId = input.contactId;
        dto.contactTypeId = input.contactTypeId;
        dto.clientStageId = stageId;
        dto.lostReasonId = input.lostReason ? input.lostReason.lostReasonId : undefined;
        dto.lostReasonNote = input.lostReason ? input.lostReason.lostReasonNote : undefined;
        return this._contactService.updateContactTypeStage(dto).pipe(
            map((res) => this.toSaveResult(res, act, stageChanged, input.taskId)),
            catchError(() =>
                of({
                    saved: false,
                    errorMessage: ClientStageCommitService.SAVE_FAILED,
                    act: act,
                    stageChanged: stageChanged,
                    taskCompleted: false,
                    transition: undefined,
                })
            )
        );
    }

    /**
     * The UpdateContactTypeStage result: IsError = true is a failed save (ErrorMessage says why); otherwise ResponseData
     * is the person's stage transition. ErrorMessage carries no completion signal — whether the save completed
     * `taskId` is read from the transition alone ({@link transitionCompletedTask}). A save that keeps the stage it
     * already had (e.g. a type-only edit on a Won client) is no transition, so it never completes anything.
     */
    private toSaveResult(
        res: ClientStageTransitionDtoIHandleError,
        act: number,
        stageChanged: boolean,
        taskId: number | undefined
    ): ClientRelationshipSaveResult {
        const saved = !!res && !res.isError;
        const message = res && res.errorMessage ? res.errorMessage.trim() : '';
        const transition = saved && res.responseData && res.responseData.isTransition ? res.responseData : undefined;

        return {
            saved: saved,
            errorMessage: saved ? null : message || ClientStageCommitService.SAVE_FAILED,
            act: act,
            stageChanged: stageChanged,
            taskCompleted: transitionCompletedTask(transition, taskId),
            transition: transition,
        };
    }
}
